import React from "react";
import { Modal, Box, Text, Table, ScrollArea, Badge, Group, ThemeIcon, Stack, Divider, Alert } from "@mantine/core";
import { IconAlertTriangle, IconCheck, IconFileAlert, IconForms, IconRefresh, IconTrash } from "@tabler/icons-react";
import type { IResultadoCargaMasiva } from "../interfaces";

interface ModalResultadoCargaMasivaVistaProps {
    abrir: boolean;
    onClose: () => void;
    resultado: IResultadoCargaMasiva | null;
    /** Título de la columna que identifica la fila rechazada (ej. "Combinación", "Mes"). */
    etiquetaReferencia: string;
    /** Mensaje destacado sobre el resultado (p. ej. por qué no se aplicó ningún cambio). */
    aviso?: string;
}

/** Resumen de la carga masiva de una matriz de configuración (mismo diseño que el de Tareas). */
const TarjetaTotal: React.FC<{ icono: React.ReactNode; etiqueta: string; valor: number; color: string }> = ({ icono, etiqueta, valor, color }) => (
    <Box
        p="xs"
        style={{
            border: '1px solid var(--mantine-color-gray-2)',
            borderRadius: 'var(--mantine-radius-md)',
            backgroundColor: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        }}
    >
        <Group gap="xs">
            {icono}
            <Text size="xs" fw={500} c="dimmed">{etiqueta}</Text>
        </Group>
        <Text fz="sm" fw={700} c={color}>{valor}</Text>
    </Box>
);

export const ModalResultadoCargaMasivaVista: React.FC<ModalResultadoCargaMasivaVistaProps> = ({ abrir, onClose, resultado, etiquetaReferencia, aviso }) => {
    const rechazadas = resultado?.Rechazadas ?? [];

    return (
        <Modal
            opened={abrir}
            onClose={onClose}
            styles={{
                header: { backgroundColor: '#0074d914' },
                body: { padding: 'var(--mantine-spacing-md)', paddingTop: 'var(--mantine-spacing-xs)', overflow: 'hidden' }
            }}
            title={
                <Group gap="xs">
                    <ThemeIcon color="primary" size="md" variant="light" radius="md">
                        <IconForms size={18} />
                    </ThemeIcon>
                    <Text fw={600} fz="md">Resumen de Carga Masiva</Text>
                </Group>
            }
            size="xl"
            radius="md"
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            zIndex={200000}
            centered
            scrollAreaComponent={Box}
        >
            <Stack gap="md" mt="xs">
                {aviso && (
                    <Alert color="#e6a23c" variant="light" radius="md" py="xs" icon={<IconAlertTriangle size={18} />}>
                        <Text size="sm">{aviso}</Text>
                    </Alert>
                )}
                <Group grow gap="sm">
                    <TarjetaTotal icono={<IconCheck size={16} color="#2f9e44" />} etiqueta="Nuevas" valor={resultado?.FilasCreadas ?? 0} color="green.7" />
                    <TarjetaTotal icono={<IconRefresh size={16} color="#0074d9" />} etiqueta="Actualizadas" valor={resultado?.FilasActualizadas ?? 0} color="primary" />
                    {(resultado?.FilasEliminadas ?? 0) > 0 && (
                        <TarjetaTotal icono={<IconTrash size={16} color="#868e96" />} etiqueta="Eliminadas" valor={resultado?.FilasEliminadas ?? 0} color="dimmed" />
                    )}
                    <TarjetaTotal icono={<IconFileAlert size={16} color="#e03131" />} etiqueta="Rechazadas" valor={resultado?.FilasRechazadas ?? 0} color="red.7" />
                </Group>

                {rechazadas.length > 0 && (
                    <Box style={{ minHeight: 0 }}>
                        <Divider label={<Text fz="xs" fw={500} c="dimmed">Detalle de Errores</Text>} labelPosition="center" mb="sm" />

                        <Box style={{ border: '1px solid #dee2e6', borderRadius: 'var(--mantine-radius-sm)', overflow: 'hidden' }}>
                            <ScrollArea h={380} type="auto" scrollbarSize={8}>
                                <Table verticalSpacing="xs" horizontalSpacing="md" withColumnBorders stickyHeader>
                                    <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#f8f9fa', boxShadow: '0 1px 0 #dee2e6' }}>
                                        <Table.Tr>
                                            <Table.Th style={{ width: 80, fontSize: '11px', background: 'inherit' }}>Fila</Table.Th>
                                            <Table.Th style={{ width: 220, fontSize: '11px', background: 'inherit' }}>{etiquetaReferencia}</Table.Th>
                                            <Table.Th style={{ fontSize: '11px', background: 'inherit' }}>Errores Encontrados</Table.Th>
                                        </Table.Tr>
                                    </thead>
                                    <Table.Tbody>
                                        {rechazadas.map((item) => (
                                            <Table.Tr key={item.Fila}>
                                                <Table.Td>
                                                    <Badge
                                                        variant="light"
                                                        color="primary"
                                                        size="md"
                                                        styles={{
                                                            root: { width: '100%', minWidth: '50px' },
                                                            label: { color: 'var(--mantine-color-dark-4)', fontSize: '11px', fontWeight: 700, overflow: 'visible' }
                                                        }}
                                                    >
                                                        #{item.Fila}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text fz="xs" fw={700}>{item.Referencia || "-"}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Stack gap={2}>
                                                        {item.Errores.map((error, eIndex) => (
                                                            <Group key={eIndex} gap={6} wrap="nowrap" align="flex-start">
                                                                <IconAlertTriangle size={12} color="var(--mantine-color-orange-6)" style={{ marginTop: 2, flexShrink: 0 }} />
                                                                <Text fz="xs" lh="xs" c="dark.3">{error}</Text>
                                                            </Group>
                                                        ))}
                                                    </Stack>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea>
                        </Box>
                    </Box>
                )}
            </Stack>
        </Modal>
    );
};
